import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./styles.css"; // External CSS
import { ThemeContext } from "../../Contexts/themeProvider";
import LazyScrollImage from "../../components/LazyScrollImage";

const SCROLL_SPEED_PX_PER_FRAME = 2.5;
const EAGER_COUNT = 6;

export default function AutoScrollSearchList({images}) {
  const theme = useContext(ThemeContext);
  const darkMode = theme.state.darkMode; 
  const [selectedImage, setSelectedImage] = useState(null);
  const scrollerRef = useRef(null);
  const directionRef = useRef(1); // 1 = down, -1 = up
  const [readyToScroll, setReadyToScroll] = useState(false);
  const eagerDoneRef = useRef(new Set());

  const visibleImages = useMemo(() => {
    return (images || []).filter((img) => img && !img.isDeleted && img.link);
  }, [images]);

  useEffect(() => {
    // Reset readiness when images change.
    eagerDoneRef.current = new Set();
    setReadyToScroll(false);
  }, [visibleImages]);

  const markEagerDone = (id) => {
    if (readyToScroll) return;
    eagerDoneRef.current.add(id);
    const need = Math.min(EAGER_COUNT, visibleImages.length);
    if (need === 0) {
      setReadyToScroll(true);
      return;
    }
    if (eagerDoneRef.current.size >= need) setReadyToScroll(true);
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    let raf = 0;
    const tick = () => {
      const node = scrollerRef.current;
      if (!node) return;

      if (readyToScroll) {
        const maxScrollTop = Math.max(0, node.scrollHeight - node.clientHeight);
        if (maxScrollTop > 0) {
          const next =
            node.scrollTop + directionRef.current * SCROLL_SPEED_PX_PER_FRAME;

          if (next >= maxScrollTop) {
            directionRef.current = -1;
            node.scrollTop = maxScrollTop;
          } else if (next <= 0) {
            directionRef.current = 1;
            node.scrollTop = 0;
          } else {
            node.scrollTop = next;
          }
        }
      }

      raf = window.requestAnimationFrame(tick);
    };

    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [readyToScroll]);

  return (
    <div className={`search-wrapper mt-5 ${!darkMode ? "dark" : "white"}`}>
      {/* Modal */}
      {selectedImage && (
        <div className="Search-backdrop" onClick={() => setSelectedImage(null)}>
          <div className="Search-content" onClick={(e) => e.stopPropagation()}>
            <div className="ModalWrapper">
              <img
                src={selectedImage.link}
                alt="Enlarged"
                className="SearchModalImage"
              />

              {/* Buttons inside image wrapper */}
              <button
                onClick={() => setSelectedImage(null)}
                className="btn-image left-btn"
              >
                ×
              </button>
              <button
                onClick={() =>
                  window.open(
                    `https://www.google.com/search?q=${encodeURIComponent(
                      selectedImage.title
                    )}`,
                    "_blank"
                  )
                }
                className="btn-image right-btn"
              >
                🔍 Search
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable image grid (auto-scroll starts after initial images load) */}
      <div className="scroll-scroller-search mt-4" ref={scrollerRef}>
        <div className="scroll-container-search">
          {visibleImages.map((img, index) => (
            <LazyScrollImage
              key={`img-${img.link}-${index}`}
              scrollerRef={scrollerRef}
              id={`img-${img.link}-${index}`}
              src={img.link}
              alt={img.title ? img.title : `Image ${index + 1}`}
              className="scroll-image"
              frameClass="search-imageFrame"
              pendingClass="search-imageFrame--pending"
              fallbackClass="search-imageFallback"
              onEagerDone={markEagerDone}
              countEagerErrorTowardReady
              eager={index < EAGER_COUNT}
              onClick={() => setSelectedImage(img)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
